/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { numberToPercentageString } from 'src/core/utils/number';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { DetectedLanguage } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';

export type LanguageCardProps = { language: DetectedLanguage };

const cardTheme = getScopedTheme('DemoPage');
const cardStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { width: '200px', minHeight: '110px' } });

export const LanguageCard = (props: LanguageCardProps) => {
    const { language } = props;

    const { localize } = useLocalization();

    return (
        Boolean(language) && (
            <Stack data-automation-id="languageCardContainer" tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>{localize(commonDemosMessages.languageDetected)}</LuisText>
                <Stack tokens={{ childrenGap: 7 }} styles={mergeStyleSets(resultCardStyles, cardStyles)}>
                    <LuisText
                        fontWeight={500}
                        data-automation-id="languageName"
                        styles={resultCardTitleStyles(cardTheme.locationEntityColor)}>
                        {language.name}
                    </LuisText>

                    <LuisText variant="smallPlus" data-automation-id="languageConfidenceScore">
                        {localize(commonDemosMessages.confidenceLabel)} {numberToPercentageString({ ratio: language.confidenceScore })}
                    </LuisText>
                </Stack>
            </Stack>
        )
    );
};
